import React, { useMemo } from 'react';
import styled from 'styled-components';
import { WorkDetailData } from '../../shared/dto/EntityRepository';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { WorkDetailInputDescription, WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { AlertPosition, AlertType, DetailType } from '../../shared/enum/EnumRepository';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useValidation } from '../../shared/hooks/useValidation';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import { openNewTab } from '../../shared/util/openNewTab';
import { parseSupportedVideoUrl } from '../../shared/util/videoUrl';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import Space from '../../shared/Space';
import { DETAIL_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';
import { getPageMessage } from '../../shared/constants/pageMessages';
import WorkDetailImg from './WorkDetailImg';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import SelectBox from '../../shared/component/SelectBox';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import orangeIcon from '../../asset/icon/orange.png';
import { imHere } from '../../shared/component/Animation';

interface WorkDetailProps {
  index: number;
  workId: string;
  data: WorkDetailData;
  priority: boolean;
}

const DETAIL_TYPES = Object.values(DetailType);
const getDetailTypeLabel = (value: DetailType) =>
  value === DetailType.VIDEO ? 'Video' : 'Image';

const WorkDetail: React.FC<WorkDetailProps> = ({ index, workId, data, priority }) => {
  const { isEditMode } = useEditMode();
  const { enqueueAlert } = useStandardAlertStore();
  const { deleteWorkDetail, toggleDetailAllowDownload } = useWorkDetailActions();
  const handleChange = useWorkViewStore((s) => s.updateActiveWorkDetailList);
  const { isValidVideoUrl } = useValidation();

  const detailType = data.detailType ?? DetailType.IMAGE;
  const isVideo = detailType === DetailType.VIDEO;
  const convertedCloudFrontUrl = useMemo(() => convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl), [data.uploadFile.originUrl]);

  const handleDelete = async () => {
    const callback = async () => {
      try {
        await deleteWorkDetail({ workId, workDetailId: data.id });
      } catch (err) { }
    }
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_DETAIL_CUT'),
      callBack: callback
    });
  };

  const moveToVideoUrl = () => {
    const videoUrl = data.videoUrl ?? '';
    if (!videoUrl) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('VIDEO_URL_NULL'),
      });
      return;
    }
    if (!isValidVideoUrl(videoUrl)) return;
    const parsedVideoUrl = parseSupportedVideoUrl(videoUrl);
    if (!parsedVideoUrl) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM', { platform: parsedVideoUrl.platform }),
      callBack: () => openNewTab(parsedVideoUrl.url.href),
    });
  };


  const handleToggleAllowDownload = async (value: boolean) => {
    const { uploadFile, ...workDetailWithoutUploadFile } = data;
    await toggleDetailAllowDownload({
      ...workDetailWithoutUploadFile,
      workId,
      updateUploadFileReq: {
        originUrl: uploadFile.originUrl,
        uploadFileId: uploadFile.id,
        allowDownload: value,
        width: uploadFile.width,
        height: uploadFile.height,
      }
    });
  };

  return (
    <WorkDetailWrapper
      id={`work-detail-${data.id}`}
      data-has-changed={data.hasChanged ? "true" : "false"}   // 편집 모드 진입 시 변경사항이 있으면 스크롤
    >
      <IndexRow>
        <WorkDetailIndex>{index}.</WorkDetailIndex>
      </IndexRow>
      {isEditMode && (
        <DetailTypeRow>
          <DetailTypeSelectWrapper>
            <SelectBox
              value={detailType}
              options={DETAIL_TYPES}
              handleChange={(value) => handleChange(data.id, { detailType: value })}
              direction={false}
              getDisplayText={getDetailTypeLabel}
              getOptionDisplayText={getDetailTypeLabel}
            />
          </DetailTypeSelectWrapper>
          {data.hasChanged && (
            <ChangedIconWrap>
              <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
            </ChangedIconWrap>
          )}
        </DetailTypeRow>
      )}
      <WorkDetailImg
        workDetailId={data.id}
        description={data.description}
        convertedCloudFrontUrl={convertedCloudFrontUrl}
        allowDownload={data.uploadFile.allowDownload}
        priority={priority}
        isVideo={isVideo}
        onPlay={isVideo ? moveToVideoUrl : undefined}
      />
      {isVideo && isEditMode && (
        <VideoInfoRow>
          <VideoInfoLabel>Video URL</VideoInfoLabel>
          <MoleculeInputDiv
            value={data.videoUrl ?? ''}
            defaultValue=""
            DefaultDiv={VideoInfoDefaultDiv}
            placeholder="YouTube or Vimeo URL only"
            handleChange={(e) => handleChange(data.id, { videoUrl: e.target.value })}
            inputStyle={WorkViewerInfo}
            StyledDiv={VideoInfoValue}
          />
        </VideoInfoRow>
      )}
      <MoleculeInputDiv
        value={data.description}
        placeholder={getPageMessage('DETAIL_DESCRIPTION_PLACEHOLDER')}
        handleChange={(e) => handleChange(data.id, { description: e.target.value })}
        inputStyle={WorkDetailInputDescription}
        StyledDiv={WorkDetailDescription}
        maxLength={DETAIL_DESCRIPTION_MAX_LENGTH}
      />
      
      {isEditMode && detailType === DetailType.IMAGE && data.uploadFile.originUrl &&
        <Space $height={"3vh"}>
          <MoleculeDivToggle
            value={data.uploadFile.allowDownload}
            name={getPageMessage('DOWNLOAD_TOGGLE_LABEL')}
            handleToggle={(e) => handleToggleAllowDownload(e.target.checked)}
          />
        </Space>
      }
      {isEditMode && (
        <DeleteRow>
          <UniversalIconBtn
            size="fit"
            border={true}
            backgroundImg={deleteCloseIcon}
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete}
          />
        </DeleteRow>
      )}
    </WorkDetailWrapper>
  );
}

const WorkDetailWrapper = styled.div`
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  margin-bottom: 8px;
`;

const IndexRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  gap: 0.5vw;
`;

const WorkDetailIndex = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const DetailTypeRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DetailTypeSelectWrapper = styled.div`
  width: clamp(70px, 23vw, 90px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const ChangedIconWrap = styled.div`
  display: flex;
  align-items: center;
  ${imHere}
`;

const ChangedIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 4px;
`;

const DeleteRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
`;

const VideoInfoRow = styled.div`
  width: 100%;
  height: 18px;
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  margin-bottom: 4px;
  ${({ theme }) => theme.typography.Body_04};
`;

const VideoInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const VideoInfoDefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const VideoInfoValue = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkDetailDescription = styled.div`
  width: 100%;
  height: 18px;

  text-align: right;
  ${({ theme }) => theme.typography.Body_03_2};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

export default WorkDetail;
